import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  HiChevronLeft,
  HiChevronRight,
  HiArrowUp,
  HiArrowDown,
  HiDownload,
} from "react-icons/hi";

import { useTranslation } from "@repo/core";
import {
  fetchScoreData,
  type ReturnsSet,
  type Strategy,
} from "@repo/api";
import { formatPercentage } from "../../../utils/numberFormat";
import { useStockDetail, useOutliers } from "../../../hooks/api/useUniversesApi";
import StockList from "./StockList";
import { useIndicatorKey } from "@repo/api";

interface PortfolioItem {
  id: string;
  date: string;
  rebalanceDay: string;
  percentage: number;
  isPositive: boolean;
}

export interface Stock {
  id: string;
  ticker: string;
  companyName: string;
  logo: string;
  percentage: number;
  isPositive: boolean;
}

interface PortfolioCarouselProps {
  strategy?: Strategy;
  portfolio?: ReturnsSet | null;
  className?: string;
  onDateChange?: (date: string) => void;
}

const PortfolioCarousel = ({
  strategy,
  portfolio,
  className = "",
  onDateChange,
}: PortfolioCarouselProps) => {
  const { tPortfolio, i18n } = useTranslation();
  const dateLocale = i18n.language || "en";
  const formatMonthYear = (date: string) =>
    new Intl.DateTimeFormat(dateLocale, { month: "short", year: "numeric" }).format(new Date(date));
  const formatFullDate = (date: string) =>
    new Intl.DateTimeFormat(dateLocale, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(date));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [contentWidth, setContentWidth] = useState("auto");
  const carouselRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { key } = useIndicatorKey();

  // Transform ReturnsSet data into carousel format
  const transformedData = useMemo(() => {
    if (!portfolio) {
      return {
        items: [],
        longStocks: [],
        shortStocks: [],
        longPerf: 0,
        shortPerf: 0,
      };
    }

    // Create portfolio items from ReturnsSet dates and Strategy performance
    const portfolioItems: PortfolioItem[] = Object.entries(portfolio).map(
      ([date, returns]) => ({
        id: date,
        date: date,
        rebalanceDay: returns.RebalanceDay,
        percentage: returns.Strategy,
        isPositive: returns.Strategy >= 0,
      })
    );

    return {
      items: portfolioItems,
      longStocks: [],
      shortStocks: [],
      longPerf: 0,
      shortPerf: 0,
    };
  }, [portfolio]);

  // Get data for the selected date
  const getSelectedDateData = useMemo(() => {
    if (!portfolio || !selectedItemId) {
      return {
        longStocks: [],
        shortStocks: [],
        longPerf: 0,
        shortPerf: 0,
      };
    }

    const selectedDateReturns = portfolio[selectedItemId];
    if (!selectedDateReturns) {
      return {
        longStocks: [],
        shortStocks: [],
        longPerf: 0,
        shortPerf: 0,
      };
    }

    // Extract long stocks for selected date
    const longStocks: Stock[] = selectedDateReturns.Longs
      ? Object.entries(selectedDateReturns.Longs)
          .sort((a, b) => b[1] - a[1])
          .map(([ticker, percentage]) => ({
            id: ticker,
            ticker: ticker,
            companyName: ticker, // Use ticker as company name for now
            logo: ticker.toLowerCase(),
            percentage: Math.abs(percentage),
            isPositive: percentage >= 0,
          }))
      : [];

    // Extract short stocks for selected date
    const shortStocks: Stock[] = selectedDateReturns.Shorts
      ? Object.entries(selectedDateReturns.Shorts)
          .sort((a, b) => b[1] - a[1])
          .map(([ticker, percentage]) => ({
            id: ticker,
            ticker: ticker,
            companyName: ticker, // Use ticker as company name for now
            logo: ticker.toLowerCase(),
            percentage: Math.abs(percentage),
            isPositive: percentage >= 0,
          }))
      : [];

    return {
      longStocks,
      shortStocks,
      longPerf: selectedDateReturns.LongLeg || 0,
      shortPerf: selectedDateReturns.ShortLeg || 0,
    };
  }, [portfolio, selectedItemId]);

  // Use transformed data if available, otherwise fall back to props or sample data
  // Use hardcoded data from transformedData
  const actualItems = transformedData.items;
  const actualLongStocks = getSelectedDateData.longStocks;
  const actualShortStocks = getSelectedDateData.shortStocks;
  const actualLongPerformance = getSelectedDateData.longPerf;
  const actualShortPerformance = getSelectedDateData.shortPerf;
  const stockDetailQuery = useStockDetail([
    ...actualLongStocks.map((s) => s.ticker),
    ...actualShortStocks.map((s) => s.ticker),
  ]);
  const stockDetails: { [key: string]: { name: string; logo: string } } =
    useMemo(() => {
      if (stockDetailQuery.data) {
        return stockDetailQuery.data.reduce(
          (acc, stock) => ({
            ...acc,
            [stock.symbol]: {
              name: stock.name,
              logo: stock.logo,
            },
          }),
          {} as { [key: string]: { name: string; logo: string } }
        );
      } else {
        return {};
      }
    }, [stockDetailQuery.data]);

  // Extract indicator name from strategy's factor field
  // Factor is a Record<string, number> like { "BookToMarket": 1.0 }
  const indicatorName = useMemo(() => {
    if (!strategy?.factor || Object.keys(strategy.factor).length === 0) {
      return undefined;
    }
    // Get the first (and typically only) factor key
    return Object.keys(strategy.factor)[0];
  }, [strategy?.factor]);

  // Fetch outliers for the indicator within the strategy's universe -
  // outlier bounds are computed per universe, so poolName is part of the lookup
  const { data: outliersData } = useOutliers(strategy?.poolName, indicatorName);

  // Sort items from newest to oldest (descending order by date) - memoized to prevent re-sorting on every render
  const portfolioItems = useMemo(() => {
    return [...actualItems].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [actualItems]);

  // Get the most recent rebalance date (first item after sorting)
  const mostRecentRebalanceDate = useMemo(() => {
    return portfolioItems.length > 0 ? portfolioItems[0].id : null;
  }, [portfolioItems]);

  // Get outlier lookup map for the current indicator
  // Only show outliers if viewing the most recent rebalance
  const outliersByTicker = useMemo(() => {
    if (!outliersData || !indicatorName) {
      return new Map();
    }

    // Only provide outliers for the most recent rebalance date
    const isViewingMostRecent = selectedItemId === mostRecentRebalanceDate;
    if (!isViewingMostRecent) {
      return new Map(); // Return empty map for historical dates
    }

    // The outliers data structure has indicator name as the top-level key
    return outliersData[indicatorName]?.byTicker ?? new Map();
  }, [outliersData, indicatorName, selectedItemId, mostRecentRebalanceDate]);
  // Fixed dimensions for portfolio items
  const ITEM_WIDTH = 128; // Fixed width in pixels (w-32 = 128px)
  const GAP_WIDTH = 16; // gap-4 = 16px
  const itemsPerView = 6; // Number of items visible at once

  // Calculate exact dimensions
  const totalContentWidth =
    portfolioItems.length * ITEM_WIDTH +
    (portfolioItems.length - 1) * GAP_WIDTH;
  const maxIndex = Math.max(0, portfolioItems.length - itemsPerView);

  // Auto-select the newest (first) item on component mount only
  useEffect(() => {
    if (portfolioItems.length > 0 && selectedItemId === null) {
      // Find the newest item (first item after sorting by newest to oldest)
      const newestItem = portfolioItems[0];
      setSelectedItemId(newestItem.id);
      onDateChange?.(newestItem.id);

      // Start at the beginning since newest items are now first
      setCurrentIndex(0);
    }
  }, [portfolioItems, selectedItemId, onDateChange]);

  const displayLongStocks = actualLongStocks.length > 0 ? actualLongStocks : [];
  const displayShortStocks =
    actualShortStocks.length > 0 ? actualShortStocks : [];
  const displayLongPerformance = actualLongPerformance;
  const displayShortPerformance = actualShortPerformance;

  // Check if scrolling is needed and set content width
  useEffect(() => {
    const shouldBeScrollable = portfolioItems.length > itemsPerView;
    setIsScrollable(shouldBeScrollable);

    // Set fixed content width based on total items
    setContentWidth(`${totalContentWidth}px`);
  }, [portfolioItems, itemsPerView, totalContentWidth]);

  const nextSlide = () => {
    setCurrentIndex((prev) => Math.min(prev + 3, maxIndex));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 3, 0));
  };

  const getSelectedItem = () => {
    return portfolioItems.find((item) => item.id === selectedItemId);
  };

  const handleItemClick = (itemId: string) => {
    setSelectedItemId(itemId);
    onDateChange?.(itemId);
  };

  const handleDownloadPortfolioData = useCallback(async () => {
    const selectedItem = portfolioItems.find(
      (item) => item.id === selectedItemId
    );

    if (!selectedItem || !strategy) return;

    try {
      const res = (await fetchScoreData(
        key,
        selectedItem.rebalanceDay
      )) as string;
      const BOM = "\uFEFF";
      const data = new Blob([BOM + res], {
        type: "text/csv;charset=utf-8",
      });
      const csvURL = window.URL.createObjectURL(data);
      const tempLink = document.createElement("a");
      tempLink.href = csvURL;
      tempLink.setAttribute(
        "download",
        `Score_${selectedItem.rebalanceDay}.csv`
      );
      tempLink.click();
    } catch (error) {
      console.log(error);
    }
  }, [strategy, portfolioItems, selectedItemId]);

  return (
    <div className={`portfolio-carousel ${className}`} ref={carouselRef}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {getSelectedItem() && (
            <div className="flex items-center gap-3">
              <div className="text-lg font-semibold text-gray-900">
                {formatFullDate(getSelectedItem()!.rebalanceDay)}
              </div>
              <button
                onClick={() => handleDownloadPortfolioData()}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
                title="Download portfolio constituents as CSV"
              >
                <HiDownload size={16} />
                <span className="hidden sm:inline">CSV</span>
              </button>
            </div>
          )}
        </div>
        {isScrollable && maxIndex > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <HiChevronLeft size={16} />
            </button>
            <button
              onClick={nextSlide}
              disabled={currentIndex >= maxIndex}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <HiChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      <div className="overflow-hidden">
        <div
          ref={contentRef}
          className="flex transition-transform duration-300 ease-in-out gap-4"
          style={{
            transform: `translateX(-${
              currentIndex * (ITEM_WIDTH + GAP_WIDTH)
            }px)`,
            width: contentWidth,
          }}
        >
          {portfolioItems.map((item) => (
            <div
              key={item.id}
              onClick={() => handleItemClick(item.id)}
              className={`flex-shrink-0 rounded-lg p-2 border cursor-pointer transition-all duration-200 hover:shadow-md ${
                selectedItemId === item.id
                  ? "bg-blue-50 border-blue-300 shadow-sm"
                  : "bg-gray-50 border-gray-200 hover:bg-gray-100"
              }`}
              style={{ width: `${ITEM_WIDTH}px` }}
            >
              <div className="text-center space-y-2">
                <div className="text-xs text-gray-600 whitespace-nowrap">
                  {formatMonthYear(item.date)}
                </div>
                <div
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    item.isPositive
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-red-100 text-red-700 border border-red-200"
                  }`}
                >
                  {item.isPositive ? (
                    <HiArrowUp size={10} />
                  ) : (
                    <HiArrowDown size={10} />
                  )}
                  {item.isPositive ? "+" : ""}
                  {formatPercentage(item.percentage)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Long and Short Stocks Section */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Long Stocks */}
        {strategy?.backtest.portfolioConstruction !== "ShortOnly" && (
          <StockList
            stocks={displayLongStocks}
            performance={displayLongPerformance}
            title={tPortfolio("longStocks")}
            stockDetails={stockDetails}
            outliersByTicker={outliersByTicker}
            factor={indicatorName}
          />
        )}

        {/* Short Stocks */}
        {strategy?.backtest.portfolioConstruction !== "LongOnly" && (
          <StockList
            stocks={displayShortStocks}
            performance={displayShortPerformance}
            title={tPortfolio("shortStocks")}
            stockDetails={stockDetails}
            outliersByTicker={outliersByTicker}
            factor={indicatorName}
          />
        )}
      </div>
    </div>
  );
};

export default PortfolioCarousel;
